import React from "react";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const BulkUploadErrorPanel = ({
  errorDetails,
  onDismiss,
  testId = "bulk-upload-error-panel",
}) => {
  const { t } = useTranslation();

  if (!errorDetails) return null;

  const { title, groups, count, hasDetails } = errorDetails;

  return (
    <div
      id={testId}
      role="alert"
      aria-live="polite"
      className="mb-3.5 overflow-hidden rounded-xl border-none bg-cfErrorBg shadow-sm"
    >
      <div className="flex items-start gap-3 px-3.5 py-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/80 text-cfErrorText">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p
                id={`${testId}-title`}
                className="text-sm font-semibold text-cfErrorText"
              >
                {title}
              </p>
              {hasDetails && (
                <p
                  id={`${testId}-count`}
                  className="mt-0.5 text-xs text-cfErrorText/80"
                >
                  {t("designs:bulk_upload_issues_found", { count })}
                </p>
              )}
            </div>

            {typeof onDismiss === "function" && (
              <button
                type="button"
                id={`${testId}-dismiss`}
                onClick={onDismiss}
                aria-label={t("designs:bulk_upload_dismiss_errors")}
                className="shrink-0 rounded-md p-1 text-cfErrorText/70 transition-colors hover:bg-white/70 hover:text-cfErrorText focus:outline-none focus-visible:ring-2 focus-visible:ring-cfErrorText focus-visible:ring-offset-1"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>

          {hasDetails && (
            <ul
              id={`${testId}-list`}
              className="mt-2.5 max-h-44 space-y-2 overflow-y-auto pr-0.5"
            >
              {groups.map((group) => (
                <li
                  key={group.row ?? "unknown"}
                  id={`${testId}-row-${group.row ?? "unknown"}`}
                  className="rounded-lg border border-[color-mix(in_srgb,var(--cf-error-text-color)_18%,transparent)] bg-white/70 px-2.5 py-2"
                >
                  {group.row !== null && group.row !== undefined && (
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-cfErrorText/70">
                      {t("designs:bulk_upload_row_label", { row: group.row })}
                    </p>
                  )}
                  <ul className="space-y-1.5">
                    {group.items.map((item) => (
                      <li
                        key={item.id}
                        id={`${testId}-item-${item.id}`}
                        className="flex flex-wrap items-start gap-1.5 text-xs leading-snug text-cfErrorText"
                      >
                        {item.field && (
                          <span className="inline-flex shrink-0 items-center rounded-md bg-[color-mix(in_srgb,var(--cf-error-text-color)_12%,white)] px-1.5 py-0.5 font-mono text-[11px] font-medium text-cfErrorText">
                            {item.field}
                          </span>
                        )}
                        <span className="min-w-0 flex-1 break-words">
                          {item.message}
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-2.5 text-xs text-cfErrorText/75">
            {t("designs:bulk_upload_fix_and_retry")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadErrorPanel;
